import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useEnergySourceTable, EnergySourceWithId } from "@/hooks/energy-sources/useEnergySourceTable";
import { useEnergySourceColumns } from "./energy-sources/columns";
import { useConfirmationDialog } from "@/hooks/energy-sources/useConfirmationDialog";
import { EnergySourceForm } from "./energy-sources/EnergySourceForm";

export const EnergySources = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedEnergySource, setSelectedEnergySource] = useState<EnergySourceWithId | null>(null);

  const {
    filteredEnergySources,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    nameFilters,
    setNameFilters,
    clearFilters,
    nameOptions,
  } = useEnergySourceTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddEnergySource = () => {
    setSelectedEnergySource(null);
    setFormOpen(true);
  };

  const handleEditEnergySource = (energySource: EnergySourceWithId) => {
    setSelectedEnergySource(energySource);
    setFormOpen(true);
  };

  const handleDeleteAction = (energySource: EnergySourceWithId) => {
    openDialog({
      type: "delete",
      energySourceId: energySource.id,
      energySourceName: energySource.name,
    });
  };

  const columns = useEnergySourceColumns(t, handleEditEnergySource, handleDeleteAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("energySources.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("energySources.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddEnergySource}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("energySources.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("energySources.searchPlaceholder")}
        filters={filters}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredEnergySources} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <EnergySourceForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        energySourceToEdit={selectedEnergySource}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={t("energySources.confirmDelete.title")}
          description={t("energySources.confirmDelete.description")}
          confirmText={
            isConfirmActionLoading
              ? t("energySources.confirmDelete.actionLoading")
              : t("energySources.confirmDelete.action")
          }
          cancelText={t("cancel")}
          variant="destructive"
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
